import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/api/client";
import { toast } from "sonner";
import { Check, Trash2, Plus, StickyNote, ListTodo, CalendarClock } from "lucide-react";

/**
 * Focus-mode inline widget for My Tasks: notepad + checklist side by side.
 * Shares the same /api/notes and /api/todos backend as the floating
 * PersonalNotesWidget — edits made here sync into the floating one immediately
 * (both are just projections of the same user-scoped documents).
 */
export default function NotesTodoInline() {
  const [note, setNote] = useState("");
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState("");
  const [newDeadline, setNewDeadline] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const noteRef = useRef("");

  const load = useCallback(async () => {
    try {
      const [n, t] = await Promise.all([api.get("/notes"), api.get("/todos")]);
      setNote(n.data?.text || "");
      noteRef.current = n.data?.text || "";
      setTodos(t.data || []);
    } catch (_e) { /* silent — reopens will retry */ }
    finally { setLoaded(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Debounced save
  useEffect(() => {
    if (!loaded || note === noteRef.current) return;
    const id = setTimeout(async () => {
      try {
        setSaving(true);
        await api.put("/notes", { text: note });
        noteRef.current = note;
      } catch { toast.error("Note not saved"); }
      finally { setSaving(false); }
    }, 700);
    return () => clearTimeout(id);
  }, [note, loaded]);

  const addTodo = async () => {
    if (!newTodo.trim()) return;
    try {
      const payload = { text: newTodo.trim() };
      if (newDeadline) payload.deadline = newDeadline;
      const { data } = await api.post("/todos", payload);
      setTodos((prev) => [...prev, data]);
      setNewTodo("");
      setNewDeadline("");
    } catch { toast.error("Couldn't add"); }
  };
  const toggleTodo = async (t) => {
    try {
      const { data } = await api.patch(`/todos/${t.id}`, { done: !t.done });
      setTodos((prev) => prev.map((x) => (x.id === t.id ? data : x)));
    } catch { toast.error("Update failed"); }
  };
  const removeTodo = async (t) => {
    try {
      await api.delete(`/todos/${t.id}`);
      setTodos((prev) => prev.filter((x) => x.id !== t.id));
    } catch { toast.error("Delete failed"); }
  };

  const pending = todos.filter((t) => !t.done).length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6" data-testid="mytasks-focus-widget">
      {/* Notes */}
      <div className="rounded-xl border border-white/8 bg-black/25 p-4 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <StickyNote size={14} className="text-amber-300" />
            <div className="overline">Focus notes · private</div>
          </div>
          <div className="text-[0.7rem] font-mono text-white/40">{saving ? "Saving…" : "Autosaved"}</div>
        </div>
        <textarea
          data-testid="mytasks-notes-textarea"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What's the plan today? Jot ideas, blockers, follow-ups. Only you can see this."
          className="flex-1 min-h-[200px] bg-black/40 border border-white/10 rounded-md p-3 text-sm text-white/85 focus:outline-none focus:ring-2 focus:ring-red-500 leading-relaxed resize-y"
        />
      </div>

      {/* To-do */}
      <div className="rounded-xl border border-white/8 bg-black/25 p-4 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ListTodo size={14} className="text-lime-300" />
            <div className="overline">To-do · private</div>
          </div>
          <div className="text-[0.7rem] font-mono text-white/50">
            {pending} pending · {todos.length - pending} done
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          <input
            data-testid="mytasks-todos-input"
            value={newTodo}
            onChange={(e) => setNewTodo(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTodo(); } }}
            placeholder="Add a to-do — press Enter"
            className="flex-1 min-w-[180px] bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          <input
            type="date"
            data-testid="mytasks-todos-deadline"
            value={newDeadline}
            onChange={(e) => setNewDeadline(e.target.value)}
            title="Optional deadline — makes this to-do surface in your My Tasks list"
            className="bg-black/40 border border-white/10 rounded-md px-2 py-2 text-xs text-white/80 [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          <button
            onClick={addTodo}
            data-testid="mytasks-todos-add-btn"
            className="bg-[#E11D2E] hover:bg-red-700 text-white rounded-md px-3 flex items-center gap-1"
          ><Plus size={14} /></button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin max-h-[280px]">
          {todos.length === 0 ? (
            <div className="text-[12px] text-white/40 italic border border-dashed border-white/8 rounded-md p-4 text-center">
              No to-dos yet. Capture your first micro-task above.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {todos.map((t) => (
                <li
                  key={t.id}
                  data-testid={`mytasks-todo-${t.id}`}
                  className={`flex items-center gap-2 rounded-md px-2 py-1.5 border transition ${
                    t.done ? "border-white/5 bg-black/25 text-white/40" : "border-white/8 bg-black/30 text-white/85"
                  }`}
                >
                  <button
                    onClick={() => toggleTodo(t)}
                    className={`h-4 w-4 rounded flex items-center justify-center border ${
                      t.done ? "bg-emerald-500/40 border-emerald-500/50" : "border-white/25 hover:border-white/50"
                    }`}
                    aria-label="Toggle done"
                  >
                    {t.done && <Check size={11} />}
                  </button>
                  <span className={`flex-1 text-sm ${t.done ? "line-through" : ""}`}>
                    {t.text}
                    {t.deadline && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[0.7rem] font-mono uppercase tracking-widest text-[#E11D2E]/90">
                        <CalendarClock size={9} /> {t.deadline}
                      </span>
                    )}
                  </span>
                  <button
                    onClick={() => removeTodo(t)}
                    className="text-white/30 hover:text-red-400"
                    aria-label="Delete"
                  ><Trash2 size={12} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
